import { cn, getCurrencyString, getCurrencyValue } from '@theprintbar/shared';
import { ReactNode } from 'react';
import { Accordion } from '../Accordion';

interface PriceBreakdown {
	total?: string | number | null;
	subtotal?: string | number | null;
	quantity?: number | null;
	breakdown?: Array<{
		title?: string | null;
		amount?: string | number | null;
		singleAmount?: string | number | null;
	}> | null;
}

interface PriceBreakdownProps {
	pricing?: PriceBreakdown;
	hideSubtotal?: boolean;
	className?: string;
	disabled?: boolean;
	/** Render the breakdown inline rather than collapsed in an accordion */
	alwaysOpen?: boolean;
	/** Shown beside the title, only when alwaysOpen */
	titleAction?: ReactNode;
}

export function PriceBreakdown({ pricing, className, disabled, alwaysOpen, titleAction, hideSubtotal }: PriceBreakdownProps) {
	if (!pricing) return null;

	const costItems = pricing?.breakdown?.filter(({ amount }) => !!amount && getCurrencyValue(amount) > 0) || [];
	const discounts = pricing?.breakdown?.filter(({ amount }) => !!amount && getCurrencyValue(amount) < 0) || [];
	const quantity = pricing?.quantity || 1;
	const title = <h5 className="text-xs uppercase">Price breakdown</h5>;

	const lines = (
		<div className="space-y-2.5">
			{costItems.map(({ title, singleAmount, amount }) => (
				<div key={title} className="grid grid-cols-5 items-center justify-between">
					<p className="text-sm col-span-3">{title}</p>
					<p className="text-sm text-right">{quantity > 1 && getCurrencyString(singleAmount)}</p>
					<p className="text-sm text-right">{getCurrencyString(amount)}</p>
				</div>
			))}
			{!!discounts.length && (
				<>
					<h5 className="text-xs uppercase">Discounts</h5>
					{discounts.map(({ title, singleAmount, amount }) => (
						<div key={title} className="grid grid-cols-5 items-center justify-between">
							<p className="text-sm col-span-3">{title}</p>
							<p className="text-sm text-right">{quantity > 1 && getCurrencyString(singleAmount)}</p>
							<p className="text-sm text-right">{getCurrencyString(amount)}</p>
						</div>
					))}
				</>
			)}
			{!hideSubtotal && (
				<div className="grid grid-cols-5 items-center justify-between">
					<p className="text-sm col-span-3"></p>
					<p className="text-sm text-right border-t border-blue pt-2 h-5">
						{quantity > 1 &&
							getCurrencyString(
								pricing?.breakdown?.reduce((acc, { singleAmount }) => acc + getCurrencyValue(singleAmount), 0),
							)}
					</p>
					<p className="text-sm text-right border-t border-blue pt-2 h-5">{getCurrencyString(pricing.total)}</p>
				</div>
			)}
		</div>
	);

	if (alwaysOpen) {
		return (
			<div className={cn('mt-8 space-y-2.5', className)}>
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-2">
						{title}
						{titleAction}
					</div>
					<div className="grid grid-cols-5 items-center justify-between">
						<p className="text-sm col-span-3"></p>
						<p className="text-sm text-right font-bold">{quantity > 1 && 'Per item'}</p>
						<p className="text-sm text-right font-bold">Total</p>
					</div>
				</div>
				{lines}
			</div>
		);
	}

	return (
		<div className={cn('mt-8', className)}>
			<Accordion disabled={disabled || !pricing.total || !costItems.length} title={title}>
				<div className="grid grid-cols-5 items-center justify-between">
					<p className="text-sm col-span-3"></p>
					<p className="text-sm text-right font-bold">{quantity > 1 && 'Per item'}</p>
					<p className="text-sm text-right font-bold">Total</p>
				</div>
				{lines}
			</Accordion>
		</div>
	);
}
